import { useState, useEffect } from 'react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ListLoadError from '../../components/admin/ListLoadError';
import api from '../../services/api';
import styles from './SubscriptionPlansPage.module.css';

// Plan code color mapping
const planColors = {
  one_individual: '#f97316',
  one_spouse: '#f97316',
  one_family: '#22c55e',
  plus: '#3b82f6',
  plus_individual: '#8b5cf6',
  plus_spouse: '#8b5cf6',
  plus_family: '#8b5cf6',
  access_individual: '#06b6d4',
  access_spouse: '#06b6d4',
  access_family: '#06b6d4',
  access_plus_individual: '#ec4899',
  access_plus_spouse: '#ec4899',
  access_plus_family: '#ec4899',
};

const getCodeColor = (code) => {
  const normalizedCode = code?.toLowerCase().replace(/-/g, '_');
  return planColors[normalizedCode] || '#6b7280';
};

// Icons
const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const ToggleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect width="20" height="12" x="2" y="6" rx="6" />
    <circle cx="8" cy="12" r="2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

// Map backend billing cycle to frontend interval
const billingCycleToInterval = {
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year'
};

// Map frontend interval to backend billing cycle
const intervalToBillingCycle = {
  month: 'monthly',
  quarter: 'quarterly',
  year: 'yearly',
  week: 'monthly', // Default to monthly for unsupported
  'one-time': 'monthly'
};

// Transform backend plan to frontend format
const transformPlanFromApi = (plan) => ({
  id: plan._id || plan.id,
  code: plan.code || '',
  name: plan.name,
  sort: plan.sortOrder || 0,
  price: plan.price / 100, // Convert cents to dollars
  interval: billingCycleToInterval[plan.billingCycle] || 'month',
  status: plan.isActive ? 'active' : 'inactive',
  description: plan.description || '',
  features: Array.isArray(plan.features) ? plan.features.join('\n') : ''
});

// Transform frontend form data to backend format
const transformPlanToApi = (formData) => ({
  name: formData.name,
  code: formData.code,
  price: Math.round(parseFloat(formData.price) * 100), // Convert dollars to cents
  billingCycle: intervalToBillingCycle[formData.interval] || 'monthly',
  sortOrder: parseInt(formData.sort, 10) || 0,
  isActive: formData.status === 'active',
  description: formData.description || '',
  features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : []
});

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [newFeature, setNewFeature] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    sort: 1,
    price: '',
    interval: 'month',
    status: 'active',
    description: '',
    features: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      const plansData = response.plans || [];
      setPlans(plansData.map(transformPlanFromApi));
      setLoadError(false);
    } catch (error) {
      console.error('Failed to load plans:', error);
      // `setPlans([])` on a FAILURE used to render "No subscription plans found."
      // beside a "Create Your First Plan" CTA — actively inviting an admin to
      // re-create a plan set that already exists. Record the failure instead.
      setPlans([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      code: '',
      name: '',
      sort: plans.length + 1,
      price: '',
      interval: 'month',
      status: 'active',
      description: '',
      features: ''
    });
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      code: plan.code,
      name: plan.name,
      sort: plan.sort,
      price: plan.price,
      interval: plan.interval,
      status: plan.status,
      description: plan.description || '',
      features: plan.features || ''
    });
    setShowModal(true);
  };

  const handleView = (plan) => {
    setViewingPlan(plan);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (plan) => {
    try {
      const response = await api.patch(`/admin/plans/${plan.id}/status`);
      if (response.plan) {
        setPlans(plans.map(p =>
          p.id === plan.id ? transformPlanFromApi(response.plan) : p
        ));
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      alert('Failed to toggle plan status');
    }
  };

  const handleDelete = async (plan) => {
    if (window.confirm(`Are you sure you want to delete "${plan.name}"?`)) {
      try {
        await api.delete(`/admin/plans/${plan.id}`);
        setPlans(plans.filter(p => p.id !== plan.id));
      } catch (error) {
        console.error('Failed to delete plan:', error);
        alert('Failed to delete plan');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const apiData = transformPlanToApi(formData);

      if (editingPlan) {
        const response = await api.put(`/admin/plans/${editingPlan.id}`, apiData);
        if (response.plan) {
          setPlans(plans.map(p =>
            p.id === editingPlan.id ? transformPlanFromApi(response.plan) : p
          ));
        }
      } else {
        const response = await api.post('/admin/plans', apiData);
        if (response.plan) {
          setPlans([...plans, transformPlanFromApi(response.plan)]);
        }
      }

      setShowModal(false);
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert(error.message || 'Failed to save plan');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Subscription Plans</h1>
          <p>Manage your subscription plans and pricing</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          <PlusIcon /> Create Plan
        </Button>
      </div>

      <div className={styles.tableCard}>
        {loadError && (
          <ListLoadError message="Could not load subscription plans" onRetry={loadPlans} />
        )}

        {!loadError && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Code</th>
              <th>Name</th>
              <th>Sort</th>
              <th>Price</th>
              <th>Interval</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, index) => (
              <tr key={plan.id}>
                <td className={styles.numberCell}>{index + 1}</td>
                <td>
                  <span
                    className={styles.codeBadge}
                    style={{ backgroundColor: getCodeColor(plan.code) }}
                  >
                    {plan.code}
                  </span>
                </td>
                <td className={styles.nameCell}>{plan.name}</td>
                <td>{plan.sort}</td>
                <td className={styles.priceCell}>${plan.price.toFixed(2)}</td>
                <td className={styles.intervalCell}>{plan.interval}</td>
                <td>
                  <span className={`${styles.statusBadge} ${styles[plan.status]}`}>
                    {plan.status}
                  </span>
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      className={`${styles.actionBtn} ${styles.view}`}
                      onClick={() => handleView(plan)}
                      title="View"
                    >
                      <ViewIcon />
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.toggle}`}
                      onClick={() => handleToggleStatus(plan)}
                      title="Toggle Status"
                    >
                      <ToggleIcon />
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.edit}`}
                      onClick={() => handleEdit(plan)}
                      title="Edit"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.delete}`}
                      onClick={() => handleDelete(plan)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

        {!loadError && plans.length === 0 && (
          <div className={styles.empty}>
            <p>No subscription plans found.</p>
            <Button variant="primary" onClick={handleCreate}>
              Create Your First Plan
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingPlan ? 'Edit Plan' : 'Create Plan'}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Plan Code</label>
              <Input
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., one_individual"
                required
              />
              <span className={styles.hint}>Unique identifier for the plan</span>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sort Order</label>
              <Input
                type="number"
                name="sort"
                value={formData.sort}
                onChange={handleChange}
                min="1"
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Plan Name</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., OVAL ONE - Individual"
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Price</label>
              <Input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Billing Interval</label>
              <select
                name="interval"
                value={formData.interval}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
                <option value="week">Weekly</option>
                <option value="one-time">One-time</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Plan description..."
              rows={3}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Features</label>
            <div className={styles.featureDndList}>
              {formData.features.split('\n').filter(f => f.trim()).map((feature, idx, arr) => (
                <div
                  key={idx}
                  className={`${styles.featureDndRow} ${dragIndex === idx ? styles.featureDndRowDragging : ''}`}
                  draggable
                  onDragStart={(e) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex === null || dragIndex === idx) return;
                    const items = arr.slice();
                    const [moved] = items.splice(dragIndex, 1);
                    items.splice(idx, 0, moved);
                    setFormData(prev => ({ ...prev, features: items.join('\n') }));
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                >
                  <span className={styles.featureDndHandle}>⠿</span>
                  <span className={styles.featureDndText}>{feature}</span>
                  <button
                    type="button"
                    className={styles.featureDndDelete}
                    onClick={() => {
                      const items = arr.slice();
                      items.splice(idx, 1);
                      setFormData(prev => ({ ...prev, features: items.join('\n') }));
                    }}
                  >×</button>
                </div>
              ))}
            </div>
            <div className={styles.featureDndAdd}>
              <input
                className={styles.featureDndInput}
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!newFeature.trim()) return;
                    setFormData(prev => ({
                      ...prev,
                      features: prev.features ? prev.features + '\n' + newFeature.trim() : newFeature.trim()
                    }));
                    setNewFeature('');
                  }
                }}
                placeholder="Add a feature..."
              />
              <button
                type="button"
                className={styles.featureDndAddBtn}
                onClick={() => {
                  if (!newFeature.trim()) return;
                  setFormData(prev => ({
                    ...prev,
                    features: prev.features ? prev.features + '\n' + newFeature.trim() : newFeature.trim()
                  }));
                  setNewFeature('');
                }}
              >Add</button>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingPlan ? 'Save Changes' : 'Create Plan'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Plan Details"
      >
        {viewingPlan && (
          <div className={styles.planDetail}>
            <div className={styles.detailHeader}>
              <span
                className={styles.codeBadge}
                style={{ backgroundColor: getCodeColor(viewingPlan.code) }}
              >
                {viewingPlan.code}
              </span>
              <span className={`${styles.statusBadge} ${styles[viewingPlan.status]}`}>
                {viewingPlan.status}
              </span>
            </div>

            <h2 className={styles.planName}>{viewingPlan.name}</h2>

            <div className={styles.priceDisplay}>
              <span className={styles.priceAmount}>${viewingPlan.price.toFixed(2)}</span>
              <span className={styles.priceInterval}>/ {viewingPlan.interval}</span>
            </div>

            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Sort Order</span>
                <span className={styles.detailValue}>{viewingPlan.sort}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Plan ID</span>
                <span className={styles.detailValue}>{viewingPlan.id}</span>
              </div>
            </div>

            {viewingPlan.description && (
              <div className={styles.detailSection}>
                <h4>Description</h4>
                <p>{viewingPlan.description}</p>
              </div>
            )}

            {viewingPlan.features && (
              <div className={styles.detailSection}>
                <h4>Features</h4>
                <ul className={styles.featureList}>
                  {viewingPlan.features.split('\n').map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={() => {
                setShowViewModal(false);
                handleEdit(viewingPlan);
              }}>
                Edit Plan
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionPlansPage;
